import { cn } from '@/lib/utils';

export interface RadarAxisData {
  label: string;
  /** 0..100 score; null treated as 0 with strikethrough label. */
  score: number | null;
  /** Optional tooltip text shown via SVG `<title>` element. */
  tooltip?: string;
}

export interface RadarChartProps {
  /** Exactly 5 entries; spec-locked order: Walk · Entertainment · Dog · Recency · Photos. */
  axes: RadarAxisData[];
  /** Outer dimensions in px. Default 280; mobile callers pass 200 or 240. */
  size?: 200 | 240 | 280;
  className?: string;
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const a = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/**
 * `<RadarChart>` — pure-SVG 5-axis radar visualizing the score profile shape.
 * Per UI-SPEC § 3.3. NOT the primary score read (the composite badge is);
 * radar shows the *shape* of strengths/weaknesses across signals.
 *
 * Anatomy:
 * - 5 concentric pentagon grid lines at 20/40/60/80/100% (`foam-50/15`)
 * - 5 axis lines from center to vertices (`foam-50/20`)
 * - Data polygon: stroke `tide-400`, fill `tide-400/20`
 * - 5 data dots at each vertex (`tide-400` fill, `foam-50` stroke)
 * - Axis labels at 1.18× radius (font 12 / weight 600 / `foam-50/80`)
 *
 * Color rules: NO bronze (radar is reference, not focal). All axes use the
 * tide-400 signal color from Phase 05.
 *
 * Null axis: renders at 0% (point at center). Polygon collapses at that
 * vertex. Axis label gets `line-through` styling.
 *
 * Mount animation: NONE — Phase 05 § 1.4 already locked one-shot bar fill
 * on dashboard cards; the detail page renders the radar at final shape.
 *
 * Responsive: outer `<svg>` always uses `viewBox="0 0 280 280"`; the `width`/
 * `height` attributes drive the rendered size (200/240/280 per breakpoint).
 *
 * ARIA: SVG has `role="img"` + comprehensive `aria-label` listing all axes.
 * Tooltips on data dots use native SVG `<title>` (browser-native; sufficient
 * per CONTEXT.md "Claude's Discretion" for the radar — Radix Tooltip not
 * required at this level of detail).
 */
export function RadarChart({ axes, size = 280, className }: RadarChartProps) {
  const cx = 140;
  const cy = 140;
  const r = 110; // leaves 30px padding for labels in the 280 viewBox
  const N = axes.length;
  // Clockwise from top: -90° starting; step 360/N (= 72 for N=5).
  const angles = axes.map((_, i) => -90 + i * (360 / N));

  // Concentric pentagon grid lines at 20/40/60/80/100%
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  const gridPolygons = gridLevels.map((lvl) =>
    angles
      .map((deg) => polarToCartesian(cx, cy, r * lvl, deg))
      .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' '),
  );

  // Axis lines (center to vertex)
  const axisLines = angles.map((deg) => polarToCartesian(cx, cy, r, deg));

  // Data polygon: clamp scores into [0, 100], null/NaN -> 0.
  const dataPoints = axes.map((axis, i) => {
    const raw = axis.score;
    const score =
      raw == null || Number.isNaN(raw) ? 0 : Math.max(0, Math.min(100, raw));
    const angle = angles[i] ?? -90;
    return polarToCartesian(cx, cy, r * (score / 100), angle);
  });
  const dataPolygon = dataPoints
    .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');

  // Label positions at 1.18× radius (axis label sits OUTSIDE the data polygon).
  const labelPositions = angles.map((deg) =>
    polarToCartesian(cx, cy, r * 1.18, deg),
  );

  const ariaLabel = `Score profile radar: ${axes
    .map((a) => `${a.label} ${a.score == null ? 'unavailable' : a.score}`)
    .join(', ')}`;

  return (
    <svg
      viewBox="0 0 280 280"
      width={size}
      height={size}
      role="img"
      aria-label={ariaLabel}
      className={cn('block', className)}
    >
      {/* Grid pentagons */}
      {gridPolygons.map((pts, i) => (
        <polygon
          key={`grid-${i}`}
          points={pts}
          fill="none"
          stroke="rgb(244 247 249 / 0.15)"
          strokeWidth={1}
        />
      ))}
      {/* Axis lines */}
      {axisLines.map((p, i) => (
        <line
          key={`axis-${i}`}
          x1={cx}
          y1={cy}
          x2={p.x}
          y2={p.y}
          stroke="rgb(244 247 249 / 0.2)"
          strokeWidth={1}
        />
      ))}
      {/* Data polygon */}
      <polygon
        points={dataPolygon}
        fill="rgb(127 174 201 / 0.2)"
        stroke="rgb(127 174 201)"
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {/* Data dots */}
      {dataPoints.map((p, i) => {
        const axis = axes[i];
        if (!axis) return null;
        const tooltipText =
          axis.tooltip ??
          `${axis.label}: ${axis.score == null ? 'Data unavailable' : `${axis.score}/100`}`;
        return (
          <circle
            key={`dot-${i}`}
            cx={p.x}
            cy={p.y}
            r={3}
            fill="rgb(127 174 201)"
            stroke="rgb(244 247 249)"
            strokeWidth={1}
          >
            <title>{tooltipText}</title>
          </circle>
        );
      })}
      {/* Axis labels */}
      {labelPositions.map((p, i) => {
        const axis = axes[i];
        if (!axis) return null;
        const isNull = axis.score == null;
        return (
          <text
            key={`label-${i}`}
            x={p.x}
            y={p.y}
            fontSize={12}
            fontWeight={600}
            fill="rgb(244 247 249 / 0.8)"
            textAnchor="middle"
            dominantBaseline="middle"
            style={isNull ? { textDecoration: 'line-through' } : undefined}
          >
            {axis.label}
          </text>
        );
      })}
    </svg>
  );
}
